import React from 'react';
import { Loader2, Check, AlertCircle, X } from 'lucide-react';
import type { DownloadItem } from '../hooks/useDownloadManager';
import {
  NetworkPingIndicator,
  formatBytesPerSec,
  buildNetworkPingHint,
} from './NetworkPingIndicator';
import './DownloadProgress.css';

function DownloadProgressBar({ item }: { item: DownloadItem }) {
  const active =
    item.status === 'pending' ||
    item.status === 'preparing' ||
    item.status === 'downloading' ||
    item.status === 'paused';
  if (!active) return null;

  const pct = Math.min(100, Math.max(0, item.progress ?? 0));
  const showRealPercent =
    (item.status === 'downloading' || item.status === 'paused') &&
    !item.streamIndeterminate &&
    Number.isFinite(item.progress);
  const indeterminatePhase =
    item.status === 'pending' ||
    item.status === 'preparing' ||
    (item.streamIndeterminate && pct <= 0);

  let barClass = 'download-progress-bar';
  let widthStyle: React.CSSProperties | undefined;

  if (indeterminatePhase) {
    barClass += ' download-progress-bar--indeterminate';
  } else if (item.streamIndeterminate && pct > 0) {
    barClass += ' download-progress-bar--indeterminate-rough';
    widthStyle = { width: `${pct}%` };
  } else {
    widthStyle = { width: `${Math.round(pct)}%` };
  }

  return (
    <div
      className="download-progress-bar-wrap"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={showRealPercent ? Math.round(pct) : undefined}
      aria-label="Progreso de descarga"
    >
      <div className={barClass} style={widthStyle} />
    </div>
  );
}

export interface DownloadProgressProps {
  downloads: DownloadItem[];
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
  onPause: (id: string) => void;
  onResume: (id: string) => void;
  onClearCompleted: () => void;
  networkHealth?: { quality: 'good' | 'slow' | 'offline' | 'unknown'; pingMs: number | null };
}

export const DownloadProgress: React.FC<DownloadProgressProps> = ({
  downloads,
  onCancel,
  onRetry,
  onPause,
  onResume,
  onClearCompleted,
  networkHealth,
}) => {
  if (downloads.length === 0) return null;

  const hasActive = downloads.some(
    (d) =>
      d.status === 'pending' ||
      d.status === 'preparing' ||
      d.status === 'downloading' ||
      d.status === 'paused'
  );

  return (
    <div className="download-progress-container" role="status" aria-live="off">
      {hasActive && (
        <div className="download-progress-site-status" title={buildNetworkPingHint(networkHealth)}>
          <NetworkPingIndicator
            health={networkHealth}
            size={14}
            showLabel
            className="download-progress-site-ping"
          />
          <span className="download-progress-site-status-label">Estado del sitio</span>
        </div>
      )}
      <div className="download-progress-list">
        {downloads.map((item) => {
          const showRealPercent =
            (item.status === 'downloading' || item.status === 'paused') &&
            !item.streamIndeterminate &&
            Number.isFinite(item.progress);
          const speedText =
            item.status === 'downloading' && item.speed != null && item.speed > 0
              ? formatBytesPerSec(item.speed)
              : '';

          return (
            <div
              key={item.id}
              className={`download-progress-item download-progress-item--${item.status}${item.status === 'completed' && item.completionWarning ? ' download-progress-item--warning' : ''}`}
              data-status={item.status}
            >
              <div className="download-progress-item-header">
                <span className="download-progress-item-title">
                  {item.options.cameraName} • {item.options.startTime.toLocaleDateString()}
                </span>
                <div className="download-progress-item-header-actions">
                  {item.status !== 'completed' && item.status !== 'error' && (
                    <button
                      type="button"
                      className="download-progress-item-action"
                      onClick={() => onCancel(item.id)}
                      aria-label="Cancelar descarga"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>

              {item.status === 'preparing' && (
                <>
                  <div className="download-progress-item-body">
                    <Loader2 size={18} className="download-progress-spinner" aria-hidden />
                    <span>Preparando conexión al servicio de exportación…</span>
                  </div>
                  <DownloadProgressBar item={item} />
                </>
              )}

              {(item.status === 'downloading' || item.status === 'paused') && (
                <div className="download-progress-item-body download-progress-stream-block">
                  <div className="download-progress-loader-row">
                    <Loader2
                      size={18}
                      className={`download-progress-spinner${item.status === 'paused' ? ' download-progress-spinner--paused' : ''}`}
                      aria-hidden
                    />
                    <span className="download-progress-loader-text">
                      {item.status === 'paused'
                        ? 'Pausado'
                        : showRealPercent
                          ? `Descargando ${Math.round(item.progress)}%`
                          : item.progress > 0
                            ? `Generando exportación ${Math.round(item.progress)}%`
                            : 'Descargando…'}
                    </span>
                  </div>
                  <DownloadProgressBar item={item} />
                  {item.status === 'downloading' && item.streamIndeterminate && (
                    <p className="download-progress-stream-label">
                      {networkHealth?.quality === 'offline'
                        ? 'Sin conexión al servidor — esperando reconexión…'
                        : networkHealth?.quality === 'slow'
                          ? `Conexión lenta (ping: ${networkHealth.pingMs}ms) — la descarga puede tardar más…`
                          : 'Recibiendo el archivo de exportación…'}
                    </p>
                  )}
                  <div className="download-progress-meta">
                    {speedText && (
                      <span
                        className="download-progress-speed-hint"
                        title={buildNetworkPingHint(networkHealth, item.speed)}
                      >
                        {speedText}
                      </span>
                    )}
                    {item.status === 'downloading' && (
                      <button
                        type="button"
                        className="download-progress-btn-text"
                        onClick={() => onPause(item.id)}
                        aria-label="Pausar"
                      >
                        Pausar
                      </button>
                    )}
                    {item.status === 'paused' && (
                      <button
                        type="button"
                        className="download-progress-btn-text"
                        onClick={() => onResume(item.id)}
                        aria-label="Reanudar"
                      >
                        Reanudar
                      </button>
                    )}
                  </div>
                </div>
              )}

              {item.status === 'completed' && (
                <div className="download-progress-item-body download-progress-completed-block">
                  <div className="download-progress-completed-row">
                    {item.completionWarning ? (
                      <AlertCircle size={20} className="download-progress-icon warning" aria-hidden />
                    ) : (
                      <Check size={20} className="download-progress-icon success" aria-hidden />
                    )}
                    <span>{item.completionMessage || 'Completado'}</span>
                  </div>
                  <div className="download-progress-completed-actions">
                    <button
                      type="button"
                      className="download-progress-item-action"
                      onClick={() => onCancel(item.id)}
                      aria-label="Cerrar"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              )}

              {item.status === 'error' && (
                <div className="download-progress-item-body">
                  <AlertCircle size={20} className="download-progress-icon error" aria-hidden />
                  <span>{item.error || 'Error en la descarga'}</span>
                  <button
                    type="button"
                    className="download-progress-btn-text"
                    onClick={() => onRetry(item.id)}
                  >
                    Reintentar
                  </button>
                  <button
                    type="button"
                    className="download-progress-item-action"
                    onClick={() => onCancel(item.id)}
                    aria-label="Cerrar"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}

              {item.status === 'pending' && (
                <>
                  <div className="download-progress-item-body">
                    <Loader2 size={18} className="download-progress-spinner" aria-hidden />
                    <span>En cola...</span>
                  </div>
                  <DownloadProgressBar item={item} />
                </>
              )}
            </div>
          );
        })}
      </div>
      {(downloads.some((d) => d.status === 'completed') || downloads.some((d) => d.status === 'error')) && (
        <button
          type="button"
          className="download-progress-clear"
          onClick={onClearCompleted}
        >
          Limpiar completados
        </button>
      )}
    </div>
  );
};
